// TODO: look into wrapping our assets calls with react-query here
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useEffect, useMemo } from 'react';

import { SortDirection } from '@ge/components/table/models/sort-direction';
import { Feature, MonitorDefs } from '@ge/models/constants';
import { fetchStorageInverters, fetchSolarInverters } from '@ge/shared/services/inverter';
import { range } from '@ge/util/array-utils';
import merge from '@ge/util/deep-merge';

import { useAuth } from './use-auth';

const STALE_THRESHOLD = 1000 * 60 * 60 * 4; // 4 hours
const PAGE_SIZE = 5000;
const BATCH_SIZE = 1;

// MVP 0
// remove this once we get permissions fully implemented
const MVP_0_FEATURE_FLAGS = Object.values(Feature).reduce(
  (featureFlags, feature) => ({
    ...featureFlags,
    [feature]: true,
  }),
  {},
);

// can revise this but currently just retrying pages once
const fetchPageSolar = async ({ feature, pageIndex, retry = true }) => {
  try {
    const { assets } = await fetchSolarInverters({
      pageIndex,
      pageSize: PAGE_SIZE,
      feature,
      sortDirection: SortDirection.ASC,
      sortMetric: 'inverterId',
    });

    return assets;
  } catch {
    if (retry) {
      return fetchPageSolar({ feature, pageIndex, retry: false });
    }
  }
};

// can revise this but currently just retrying pages once
const fetchPage = async ({ feature, pageIndex, retry = true }) => {
  try {
    const { assets } = await fetchStorageInverters({
      pageIndex,
      pageSize: PAGE_SIZE,
      feature,
      sortDirection: SortDirection.ASC,
      sortMetric: 'inverterId',
    });

    return assets;
  } catch {
    if (retry) {
      return fetchPage({ feature, pageIndex, retry: false });
    }
  }
};

// TODO: move batch fetch into its own hook that can be used by different features
export const useStorageInverters = ({
  enabled = false,
  inverterType = MonitorDefs.STORAGE_SITES,
}) => {
  // data hooks
  const { permissions } = useAuth();

  // store
  const {
    updateAssets,
    setError,
    setIsLoaded,
    setIsLoading,
    setLastUpdated,
    setInverterLastUpdated,
    solarInverterLastUpdated,
    setSolarInverterLastUpdated,
  } = useStoreActions((actions) => actions.assets);
  // not destructuring here in case we lose change detection by mangling reference
  const appState = useStoreState((state) => state.app);
  const state = useStoreState((state) => state.assets);

  const buildBatch = (feature, startIdx) =>
    range(BATCH_SIZE, startIdx).map((pageIndex) => fetchPage({ feature, pageIndex }));

  const buildBatchSolar = (feature, startIdx) =>
    range(BATCH_SIZE, startIdx).map((pageIndex) => fetchPageSolar({ feature, pageIndex }));

  useEffect(() => {
    // data is not stale
    if (!enabled || state.isLoading) {
      return;
    }

    if (inverterType === MonitorDefs.STORAGE_SITES) {
      if (new Date().getTime() - state.inverterLastUpdated < STALE_THRESHOLD) {
        setIsLoaded(true);
        return;
      }
      const load = async () => {
        try {
          // manage asset load internally and update store once to avoid unnecessary re-renders
          let loadMap = {};

          const loadFeature = async (feature) => {
            let pageIndex = 0;
            let hasNextPage = true;

            while (hasNextPage) {
              try {
                const batchResults = await Promise.allSettled(buildBatch(feature, pageIndex));

                batchResults.forEach(({ value }) => {
                  value?.forEach((_asset) => {
                    // MVP 0
                    // add all features to entity permissions for now because service doesn't
                    // distinguish currently
                    const asset = {
                      ..._asset,
                      featureFlags: MVP_0_FEATURE_FLAGS,
                    };

                    const existingAsset = loadMap[asset.id];
                    loadMap[asset.id] = !existingAsset ? asset : merge(existingAsset, asset);
                  });
                });

                // try to push forward eagerly by only evaluating successful calls to see if we hit the last page
                // TOOD: revisit this approach
                const fulfilled = batchResults.filter(({ value }) => value);
                hasNextPage =
                  Boolean(fulfilled.length) &&
                  fulfilled.every(({ value }) => value?.length >= PAGE_SIZE);
                pageIndex += BATCH_SIZE;
              } catch (err) {
                console.error(err);

                setError(err);

                // TODO: figure out how to handle partial failures
              }
            }
          };

          setIsLoading(true);

          // TODO - are we really always fetching using only the common token?
          await loadFeature(Feature.COMMON);

          updateAssets(Object.values(loadMap));
          setInverterLastUpdated();
          setIsLoaded(true);
        } catch (err) {
          console.error(err);

          setError(err);
        } finally {
          setIsLoading(false);
        }
      };

      load();
    } else {
      if (new Date().getTime() - state.solarInverterLastUpdated < STALE_THRESHOLD) {
        setIsLoaded(true);
        return;
      }
      const loadSolar = async () => {
        try {
          // manage asset load internally and update store once to avoid unnecessary re-renders
          let loadMap = {};

          const loadFeature = async (feature) => {
            let pageIndex = 0;
            let hasNextPage = true;

            while (hasNextPage) {
              try {
                const batchResults = await Promise.allSettled(buildBatchSolar(feature, pageIndex));

                batchResults.forEach(({ value }) => {
                  value?.forEach((_asset) => {
                    // MVP 0
                    // add all features to entity permissions for now because service doesn't
                    // distinguish currently
                    const asset = {
                      ..._asset,
                      featureFlags: MVP_0_FEATURE_FLAGS,
                    };

                    const existingAsset = loadMap[asset.id];
                    loadMap[asset.id] = !existingAsset ? asset : merge(existingAsset, asset);
                  });
                });

                // try to push forward eagerly by only evaluating successful calls to see if we hit the last page
                // TOOD: revisit this approach
                const fulfilled = batchResults.filter(({ value }) => value);
                hasNextPage =
                  Boolean(fulfilled.length) &&
                  fulfilled.every(({ value }) => value?.length >= PAGE_SIZE);
                pageIndex += BATCH_SIZE;
              } catch (err) {
                console.error(err);

                setError(err);

                // TODO: figure out how to handle partial failures
              }
            }
          };

          setIsLoading(true);

          // TODO - are we really always fetching using only the common token?
          await loadFeature(Feature.COMMON);

          updateAssets(Object.values(loadMap));
          setSolarInverterLastUpdated();
          setIsLoaded(true);
        } catch (err) {
          console.error(err);

          setError(err);
        } finally {
          setIsLoading(false);
        }
      };

      loadSolar();
    }
  }, [
    state.isLoaded,
    state.isLoading,
    appState.isPopout,
    permissions,
    updateAssets,
    enabled,
    inverterType,
    setError,
    setIsLoaded,
    setIsLoading,
    state.lastUpdated,
    setLastUpdated,
    setInverterLastUpdated,
    state.inverterLastUpdated,
    state.solarInverterLastUpdated,
    solarInverterLastUpdated,
    setSolarInverterLastUpdated,
  ]);

  return useMemo(
    () => ({
      data: !(state.error || state.isLoading) ? state.allAssets : null,
      error: state.error,
      isLoading: state.isLoading,
    }),
    [state.allAssets, state.error, state.isLoading],
  );
};
